<template>
	<div class="content-area__content">
		<vue-title :title="titleText"></vue-title>
		<h1 class="section-title" data-test-element="changeusername-title">{{ titleText }}</h1>

		<Form v-slot="{ validate, setErrors, setFieldError }">
			<form ref="form" action="authentication/changeusername" @submit.prevent="validateBeforeSubmit(validate, setErrors, setFieldError)">
				<!-- Server error summary (unchanged behavior) -->
				<div class="validation-summary-errors alert alert-danger fade show"
						 role="alert"
						 data-valmsg-summary="true"
						 v-show="model.errorMessage && !hasAnyFieldErrors"
						 data-test-element="idp-alert-danger-generic">
					<i class="material-icons">warning</i>
					<span data-test-element="changeusername-errormessage">{{ model.errorMessage }}</span>
				</div>

				<!-- Client/server validation summary -->
				<div class="validation-summary-errors alert alert-danger fade show"
						 role="alert"
						 data-valmsg-summary="true"
						 v-show="hasAnyFieldErrors || hasError">
					<i class="material-icons">warning</i>
					<span v-if="formSummaryMessage">{{ formSummaryMessage }}</span>
					<span v-else class="alert-message" data-test-element="alert-message">{{ $t('validation:summary') }}</span>
				</div>

				<fieldset class="panel">
					<div class="panel-message" data-test-element="changeusername-instructionmessage">
						{{ instructionsText }}
					</div>

					<!-- New Username -->
					<div class="form-group">
						<label v-on:click.stop>
							<div class="newusername-label-row" data-test-element="changeusername-label-newusername">
								<span class="label">{{ newUsernameLabel }}</span>
								<contextual-help-component class="newusername-help"
																					 :text="model.contextualHelp"
																					 data-test-element="changeusername-contextualHelp"
																					 tabindex="0">
								</contextual-help-component>
							</div>

							<span class="control-with-help-offset">
								<Field name="NewUsername" :label="newUsernameLabel" :rules="{ required: true }" v-model="model.newUsername" v-slot="{ field, meta }">
									<input v-bind="field"
												 name="NewUsername"
												 id="newusername"
												 v-focus
												 class="control -with-help"
												 data-test-element="changeusername-newusername"
												 @change="onNewUserNameChange"
												 autocomplete="off"
												 :class="{ 'input-validation-error': submitted && !meta.valid }" />
								</Field>
							</span>
						</label>

						<ErrorMessage v-if="submitted" name="NewUsername" v-slot="{ message }">
							<span class="field-validation-error" data-test-element="changeusername-newusername-validationerror">{{ message }}</span>
						</ErrorMessage>

						<span class="field-validation-error"
									data-test-element="changeusername-newusername-customvalidationerror"
									id="newusername-validationerror"
									style="display:none;"></span>
					</div>

					<!-- Confirm Username -->
					<div class="form-group">
						<label>
							<span class="label" data-test-element="changeusername-label-confirmusername">
								{{ confirmUsernameLabel }}
							</span>
							<span class="control-with-help-offset">
								<Field name="ConfirmUsername" :label="confirmUsernameLabel" :rules="confirmRules" v-model="model.confirmUsername" v-slot="{ field, meta }">
									<input v-bind="field"
												 name="ConfirmUsername"
												 class="control"
												 data-test-element="changeusername-confirmusername"
												 autocomplete="off"
												 :class="{ 'input-validation-error': submitted && !meta.valid }" />
								</Field>
							</span>
						</label>

						<ErrorMessage v-if="submitted" name="ConfirmUsername" v-slot="{ message }">
							<span class="field-validation-error" data-test-element="changeusername-confrimusername-validationerror">{{ message }}</span>
						</ErrorMessage>
					</div>

					<div class="form-actions">
						<button type="submit" name="Action:ChangeUsername" class="button" data-test-element="changeusername-continuebutton" :disabled="disabled">{{ $t('shared:action:continue') }}</button>
						<a @click.prevent="loginCancel" href="authentication/logincancel" data-test-element="changeusername-cancellink" class="button-secondary link-text-transform">{{ $t('shared:action:cancel') }}</a>
					</div>
				</fieldset>
			</form>
		</Form>
	</div>
</template>

<script>
	import redirectHandler from 'utils/redirect';
	import ContextualHelp from 'components/popper-help';
	import { Form, Field, ErrorMessage } from 'vee-validate';
	import validation from 'utils/validation-mixin';
	import { applyServerErrorsFromAxios, focusFirstInvalid } from 'utils/validation-mixin';
	import { fmt } from 'utils/i18n-format';

	export default {
		name: 'ChangeUsername',
		components: {
			'contextual-help-component': ContextualHelp,
			Form,
			Field,
			ErrorMessage
		},
		mixins: [validation],

		data: () => ({
			model: {
				newUsername: null,
				confirmUsername: null,
				contextualHelp: null,
				// server-provided validation config:
				emailRegex: null,
				usernamePattern: null,
				enabledUserNameEmailValidation: false,
				emailValidationMessage: null,
				usernamePatternMessage: null
			},
			hasError: false,
			disabled: false,
			submitted: false,
			hasAnyFieldErrors: false,
			formSummaryMessage: null
		}),

		computed: {
      usernameLabel() {
        return this.$t('shared:fields:username');
      },
      titleText() {
        return fmt(this.$t('identityProvider:changeUsername:title'), this.usernameLabel);
      },
      instructionsText() {
        return fmt(this.$t('identityProvider:changeUsername:instructions'), String(this.usernameLabel).toLowerCase());
      },
      newUsernameLabel() {
        return fmt(this.$t('identityProvider:changeUsername:newUsername'), this.usernameLabel);
      },
      confirmUsernameLabel() {
        return fmt(this.$t('identityProvider:changeUsername:confirmUsername'), this.usernameLabel);
      },
      confirmRules() {
        return { required: true, confirmed: ['@NewUsername', this.newUsernameLabel] };
      }
		},

		methods: {
			onNewUserNameChange() {
				$('#newusername-validationerror').text('').css('display', 'none');
				$('#newusername').removeClass('input-validation-error');
				this.hasError = false;
			},

			displayErrorMessage(errorMesage) {
				$('#newusername-validationerror').text(errorMesage).css('display', 'block');
				$('#newusername').addClass('input-validation-error');
				this.hasError = true;
			},

			validateUsername() {
				const emailExpression = new RegExp(this.model.emailRegex);
				const invalidUsername = new RegExp(this.model.usernamePattern);

				if (this.model.enabledUserNameEmailValidation && emailExpression.test(this.model.newUsername)) {
					this.displayErrorMessage(this.model.emailValidationMessage);
					return true;
				} else if (!invalidUsername.test(this.model.newUsername)) {
					this.displayErrorMessage(this.model.usernamePatternMessage);
					return true;
				}
				return false;
			},

			async validateBeforeSubmit(validate, setErrors, setFieldError) {
				// reset summary flags
				this.model.errorMessage = null;
				this.formSummaryMessage = null;
				this.hasAnyFieldErrors = false;
				this.hasError = false;
				this.submitted = true;

				// vee-validate v4: validate() => { valid, results } or true
				const result = await validate();
				const isValid = result === true || result?.valid === true;

				if (!isValid) {
					this.hasAnyFieldErrors = true;
					focusFirstInvalid(this.$refs.form);
					return;
				}

				// run custom regex/email validation (legacy behavior)
				if (this.validateUsername()) {
					// focus the NewUsername input when custom validation fails
					const el = document.getElementById('newusername');
					if (el && typeof el.focus === 'function') el.focus();
					this.hasAnyFieldErrors = true;
					return;
				}

				await this.post(setErrors, setFieldError);
			},

			async post(setErrors, setFieldError) {
				const form = this.$refs.form;
				this.disabled = true;
				try {
					const response = await this.$http.post(form.action, this.model);

          // reset UI before redirect to avoid validation flicker
          this.submitted = false;
          this.hasAnyFieldErrors = false;
          this.hasError = false;
          this.formSummaryMessage = null;
          this.model.errorMessage = null;

          if (typeof setErrors === 'function') {
            setErrors({});
          }
          if (typeof setFieldError === 'function') {
            setFieldError('NewUsername', undefined);
            setFieldError('ConfirmUsername', undefined);
          }

					this.model = response.data;
					redirectHandler.checkAndRedirect(response, this.$router);
				} catch (error) {
					const resp = error?.response;

          // Primary path for Vue 3 + vee-validate v4
          if (applyServerErrorsFromAxios(error, { setErrors, setFieldError })) {
            const bag = resp?.data?.errors;
            const s = bag && (bag[''] || bag['.']);
            const m = Array.isArray(s) ? s.find(Boolean) : s;
            this.formSummaryMessage = m || null;
            this.hasAnyFieldErrors = true;
            focusFirstInvalid(form);
            return;
          }

          // Legacy fallback only (guarded)
          try {
            if (this.validateResponse?.(form, resp)) {
              this.hasAnyFieldErrors = true;
              focusFirstInvalid(form);
              return;
            }
          } catch {
            // ignore legacy-path failures
          }

					throw error;
				} finally {
					this.disabled = false;
					// reset legacy custom error flag after a submit cycle
					this.hasError = false;
				}
			},

			async loginCancel(event) {
				const response = await this.$http.post(event.target.href);
				redirectHandler.checkAndRedirect(response, this.$router);
			}
		},

		async mounted() {
			const form = this.$refs.form;
			const response = await this.$http.get(form.action);
			this.model = response.data;
		}
	};
</script>